import { FilledInputProps } from '@mui/material/FilledInput';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import React, { ReactElement, useId } from 'react';
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  useFormContext,
} from 'react-hook-form';

import { useFormProviderRCPropsContext } from '@/components/form/FormProviderRC.comp.tsx';
import { handleKeyDownDefault } from '@/components/form/util/handleKeyDownDefault.util.ts';
import { isFormFieldRequired } from '@/utils/form/isFormFieldRequired.util.ts';

import { FormPropsModel } from './model/FormProps.model';
import { TextError } from './TextError.comp';

export interface TextFormFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends FormPropsModel<TName> {
  EndIcon?: React.ReactNode;
  helperText?: string;
  /** Turns off the fallback of the placeholder to the label. */
  hidePlaceholder?: boolean;
  maxLength?: number;
  maxRows?: number;
  minRows?: number;
  multiline?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onChangeNotStopping?: (event?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  StartIcon?: React.ReactNode;
  step?: 'any' | number;
  type?: React.InputHTMLAttributes<unknown>['type'];
  value?: string;
}

export const TextFormField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(
  props: TextFormFieldProps<TFieldValues, TName>,
): ReactElement<TextFormFieldProps<TFieldValues, TName>> => {
  const { control } = useFormContext<TFieldValues>();
  const {
    EndIcon,
    fullWidth,
    helperText,
    hidePlaceholder,
    id,
    label,
    maxLength,
    maxRows,
    minRows,
    multiline,
    name,
    onBlur,
    onChange,
    onChangeNotStopping,
    placeholder,
    StartIcon,
    step,
    ...rest
  } = props;
  const { validation } = useFormProviderRCPropsContext();
  const errorId = useId();
  const render: ControllerProps<TFieldValues, TName>['render'] = ({
    field,
    fieldState: { error },
  }) => {
    const isRequired = isFormFieldRequired(field.name, validation);

    const handleChange: FilledInputProps['onChange'] = event => {
      if (onChange) {
        onChange(event);
      } else {
        onChangeNotStopping?.(event);
        field.onChange(event);
      }
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      field.onBlur();
      onBlur?.(event);
    };

    return (
      <FormControl fullWidth={fullWidth ?? true}>
        <TextField
          key={name}
          {...field}
          {...rest}
          {...(label && {
            InputLabelProps: {
              disableAnimation: false,
              shrink: true,
            },
            label,
            sx: {
              ...rest.sx,
              fieldset: {
                background: 'none',
              },
            },
          })}
          error={Boolean(error)}
          hiddenLabel={!label}
          helperText={error ? undefined : helperText}
          id={id && `input-${id}`}
          maxRows={maxRows ?? (minRows ?? 0) + 2}
          minRows={minRows}
          multiline={multiline}
          onBlur={handleBlur}
          onChange={handleChange}
          onKeyDown={handleKeyDownDefault({ ignore: multiline })}
          placeholder={hidePlaceholder ? undefined : (placeholder ?? label)}
          required={isRequired}
          size={'small'}
          slotProps={{
            htmlInput: { ...(error && { 'aria-describedby': errorId }), step },
            input: {
              endAdornment: EndIcon && <InputAdornment position="end">{EndIcon}</InputAdornment>,
              startAdornment: StartIcon && (
                <InputAdornment position="start">{StartIcon}</InputAdornment>
              ),
            },
          }}
          variant="standard"
        />
        <TextError error={error} id={errorId} />
      </FormControl>
    );
  };

  return (
    <Controller<TFieldValues, TName>
      control={control}
      name={name}
      render={render}
      rules={{
        maxLength,
      }}
    />
  );
};
